import React, { useState } from "react";
import {
  getSimilarDays,
  SimilarDaysResponse,
} from "../../../Rest_api/RestApi";
import { SearchBox } from "../../../components/Search";
import { DynamicDatePicker } from "../../../components/DatePicker";

const METHOD_OPTIONS = [
  { value: "euclidean", label: "Euclidean" },
  { value: "dtw", label: "Dynamic Time Warping (DTW)" },
  // { value: "weighted_euclidean", label: "Weighted Euclidean" },
];

const FEATURE_OPTIONS = [
  { value: "temperature_2m", label: "Temperature (°C)" },
  { value: "relative_humidity_2m", label: "Humidity (%)" },
  { value: "precipitation", label: "Precipitation (mm)" },
  { value: "rain", label: "Rain (mm)" },
  { value: "cloud_cover", label: "Cloud Cover (%)" },
  { value: "wind_speed_80m", label: "Wind 80m (km/h)" },
  { value: "sunshine_duration", label: "Sunshine Duration (s)" },
  { value: "direct_radiation_instant", label: "Direct Radiation Instant (W/m²)" },
];

type Option = { value: string; label: string };

interface Props {
  targetDate: string;
  locationId: number;
  onClose: () => void;
  persistedResult: SimilarDaysResponse | null;
  onResultUpdate: (result: SimilarDaysResponse | null) => void;
  onDateSelect: (dateStr: string) => void;
  onDateRemove: (dateStr: string) => void;
  selectedDates: Set<string>;
  features: Option[];
  onFeaturesChange: (features: Option[]) => void;
}

const SimilarDaysPopup: React.FC<Props> = ({
  targetDate,
  locationId,
  onClose,
  persistedResult,
  onResultUpdate,
  onDateSelect,
  onDateRemove,
  selectedDates,
  features,
  onFeaturesChange,
}) => {
  const initDate = new Date(targetDate);
  const [selectedDate, setSelectedDate] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  }>({ startDate: initDate, endDate: initDate, key: "selection" });

  const [method, setMethod] = useState("euclidean");
  const [topK, setTopK] = useState(5);
  const [extraHolidays, setExtraHolidays] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatYmd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const handleFind = async () => {
    setLoading(true);
    setError(null);
    onResultUpdate(null);
    const now = new Date();
    const fiveYearsAgo = new Date(now);
    fiveYearsAgo.setFullYear(now.getFullYear() - 5);
    try {
      const res = await getSimilarDays({
        target_date: formatYmd(selectedDate.startDate),
        location_id: locationId,
        start_date: formatYmd(fiveYearsAgo),
        end_date: formatYmd(now),
        method,
        top_k: topK,
        features: features.map((f) => f.value).join(",") || undefined,
      });
      if (!res || !Array.isArray(res.results)) throw new Error("No response from server");
      onResultUpdate(res);
    } catch (e: any) {
      setError(e?.message ?? "Failed to fetch similar days");
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (dateStr: string) => {
    if (selectedDates.has(dateStr)) {
      onDateRemove(dateStr);
    } else {
      onDateSelect(dateStr);
    }
  };

  const visibleResults = persistedResult?.results ?? [];

  return (
    <div
      className="sd-overlay"
      // onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="sd-panel">
        {/* Header */}
        <div className="sd-header">
          <span className="sd-title">Find Similar Days</span>
          <button className="sd-close" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 2l12 12M14 2L2 14" />
            </svg>
          </button>
        </div>

        {/* Controls */}
        <div className="sd-controls">
          {/* Row 1 */}
          <div className="sd-row1">
            <div className="sd-field">
              <span className="sd-label">Method</span>
              <select
                className="sd-select"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              >
                {METHOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="sd-field">
              <span className="sd-label">Top K</span>
              <input
                type="number"
                className="sd-input-num"
                min={1}
                max={100}
                value={topK}
                onChange={(e) =>
                  setTopK(Math.max(1, Math.min(100, Number(e.target.value))))
                }
              />
            </div>

            <div className="sd-field">
              <span className="sd-label">Target Date</span>
              <div className="sd-datepicker-wrap">
                <DynamicDatePicker
                  defaultDates={selectedDate}
                  setDate={(dates) => {
                    const range = dates[0] ?? selectedDate;
                    setSelectedDate({ ...range, endDate: range.startDate });
                  }}
                  singleDate={true}
                />
              </div>
            </div>

            <div className="sd-field sd-field--grow">
              <span className="sd-label">Similarity Variables</span>
              <SearchBox
                options={FEATURE_OPTIONS}
                selectedOptions={features}
                onChange={(s) => onFeaturesChange(s ? [s as Option] : [])}
                placeholder="Select variables"
                isMany={false}
                minWidth={200}
                maxWidth={400}
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="sd-row2">
            <div className="sd-field sd-field--grow">
              <span className="sd-label">Extra Holidays</span>
              <input
                type="text"
                className="sd-input-text"
                placeholder="e.g. 2026-01-26, 2026-08-15"
                value={extraHolidays}
                onChange={(e) => setExtraHolidays(e.target.value)}
              />
            </div>

            <button
              className="sd-btn-find"
              onClick={handleFind}
              disabled={loading}
            >
              {loading ? "Finding…" : "Find Similar Days"}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && <div className="sd-error">{error}</div>}

        {/* Results */}
        {persistedResult && (
          <div className="sd-results">
            <div className="sd-results-meta">
              <span>Location: <strong>{persistedResult.location_name}</strong></span>
              <span>Method: <strong>{persistedResult.method}</strong></span>
              <span>Features: <strong>{persistedResult.features?.join(", ")}</strong></span>
              <span className="sd-hint">Click a row to add it to Historical &amp; Weather Dates</span>
            </div>
            <div
              className="sd-table-wrap"
              style={{ userSelect: "none" }}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onContextMenu={(e) => e.preventDefault()}
            >
              <table className="sd-table">
                <colgroup>
                  <col /><col /><col /><col /><col />
                </colgroup>
                <thead>
                  <tr>
                    <th className="sd-th-rank">Rank</th>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Distance</th>
                    <th className="sd-th-added"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleResults.map((r, idx) => {
                    const isAdded = selectedDates.has(r.date);
                    return (
                      <tr
                        key={r.date}
                        className={`sd-row-clickable${isAdded ? " sd-row--added" : ""}`}
                        onClick={() => handleRowClick(r.date)}
                        title="Click to add to Weather Dates"
                      >
                        <td className="sd-td-rank">{idx + 1}</td>
                        <td>{r.date}</td>
                        <td>
                          <span className={`sd-cat sd-cat--${r.category}`}>
                            {r.category}
                          </span>
                        </td>
                        <td>{typeof r.distance === "number" ? r.distance.toFixed(6) : "--"}</td>
                        <td className="sd-td-tick">
                          {isAdded && (
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#16a34a" strokeWidth="2.2">
                              <path d="M2.5 8.5l4 4 7-8" />
                            </svg>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {visibleResults.length === 0 && (
                    <tr>
                      <td colSpan={5} className="sd-empty">No results found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimilarDaysPopup;
